import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Token } from '../types/api';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: Token, user: User) => void;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('access_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setRefreshToken(storedRefreshToken);
          setUser(JSON.parse(storedUser));
          
          // Verify token is still valid by fetching current user
          const currentUser = await apiService.getCurrentUser();
          setUser(currentUser);
        } catch (error) {
          // Try to refresh the token
          if (storedRefreshToken) {
            try {
              console.log('Attempting to refresh token...');
              const newTokenData = await apiService.refreshToken(storedRefreshToken);
              console.log('Token refresh successful:', newTokenData);
              
              setToken(newTokenData.access_token);
              setRefreshToken(newTokenData.refresh_token);
              localStorage.setItem('access_token', newTokenData.access_token);
              localStorage.setItem('refresh_token', newTokenData.refresh_token);
              
              // Verify the new token
              const currentUser = await apiService.getCurrentUser();
              setUser(currentUser);
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              // Refresh failed, clear storage
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('user');
              setToken(null);
              setRefreshToken(null);
              setUser(null);
            }
          } else {
            // No refresh token, clear storage
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = (tokenData: Token, userData: User) => {
    localStorage.setItem('access_token', tokenData.access_token);
    if (tokenData.refresh_token) {
      localStorage.setItem('refresh_token', tokenData.refresh_token);
      setRefreshToken(tokenData.refresh_token);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(tokenData.access_token);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Even if logout fails, clear local state
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setToken(null);
      setRefreshToken(null);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 